/**
 * Portfolio module — shared constants and row helpers.
 *
 * The Portfolio reuses the watchlist's table, heatmap and filter components
 * verbatim. What it does NOT reuse is their localStorage keys: sharing those
 * would make the two pages' cached results and "Compare to" toggle move in
 * lockstep, which is confusing when they hold different positions. Every
 * persisted key the Portfolio touches is namespaced here.
 */

export const ASSET_TYPES = { OPTION: 'option', STOCK: 'stock' };

// Cache for the portfolio's last results. Same 30-minute TTL as the watchlist —
// positions move often enough that a longer cache goes stale.
export const PORTFOLIO_CACHE_KEY = 'portfolio_last_data';
export const PORTFOLIO_CACHE_TIMESTAMP_KEY = 'portfolio_last_data_timestamp';
export const PORTFOLIO_CACHE_TTL_MINUTES = 30;

// Portfolio's own baseline ("Compare to: Avg Cost | Current Premium") setting,
// deliberately separate from the watchlist's `watchlist_baseline_mode`.
export const PORTFOLIO_BASELINE_MODE_KEY = 'portfolio_baseline_mode';

// Per-section collapse state. Each section persists independently so the page
// reopens exactly as the user left it. Values are the strings 'true'/'false'
// (CollapsibleSection handles the read/write).
export const PORTFOLIO_COLLAPSE_KEYS = {
  ADD: 'portfolio_section_add_expanded',
  HOLDINGS: 'portfolio_section_holdings_expanded',
  SIMULATION: 'portfolio_section_simulation_expanded',
  INTEL: 'portfolio_section_intel_expanded',
};

// Company Intel — the last ticker the user was looking at, so returning to the
// page doesn't reset them to the first holding alphabetically.
export const PORTFOLIO_INTEL_TICKER_KEY = 'portfolio_intel_selected_ticker';
export const PORTFOLIO_INTEL_TAB_KEY = 'portfolio_intel_active_tab';

// What the selected holding is analysed alongside.
//   NONE        — the holding on its own
//   COMPETITORS — its saved PortfolioPeerSet (the original behavior, so this
//                 stays the default; anything else would change what existing
//                 users see on their next visit)
//   HOLDINGS    — other positions in this portfolio, chosen from pills
export const INTEL_COMPARE_MODES = {
  NONE: 'none',
  COMPETITORS: 'competitors',
  HOLDINGS: 'holdings',
};

// One mode for the whole section, plus the holdings picked in HOLDINGS mode
// (stored as a JSON array).
export const PORTFOLIO_INTEL_COMPARE_MODE_KEY = 'portfolio_intel_compare_mode';
export const PORTFOLIO_INTEL_HOLDING_PEERS_KEY = 'portfolio_intel_holding_peers';

/**
 * Ceiling on comparison tickers, in either mode.
 *
 * MUST stay in sync with MAX_PEERS in contracts/views/portfolio_intel.py, which
 * enforces the same limit on saved sets. It is not just a readability rule: the
 * research/stock-analyzer endpoints set no ticker cap of their own and pace
 * themselves with per-ticker time.sleep calls, so an uncapped comparison across
 * a large portfolio becomes one very long request with no axios timeout to
 * catch it.
 */
export const MAX_COMPARE_PEERS = 8;

/**
 * True when a saved position is a plain equity holding rather than an option.
 * Accepts both the API's snake_case payload and a Title-Case simulation row.
 * Falls back to 'option' so anything saved before stocks existed reads as an
 * option, matching the backend's column default.
 */
export function isStockRow(row) {
  if (!row) return false;
  const assetType = row.asset_type ?? row['Asset Type'];
  return assetType === ASSET_TYPES.STOCK;
}

/**
 * The distinct tickers across a set of holdings, sorted.
 *
 * This is what makes Company Intel different from the Research tool: the ticker
 * set is derived from what you own rather than typed in every visit. Several
 * positions on the same underlying collapse to one entry.
 */
export function uniqueTickers(contracts) {
  const seen = new Set();
  (Array.isArray(contracts) ? contracts : []).forEach((c) => {
    const ticker = c?.ticker;
    if (ticker) seen.add(String(ticker).toUpperCase());
  });
  return Array.from(seen).sort();
}

/** Split a mixed list of positions into its option and stock halves. */
export function splitByAssetType(rows) {
  const options = [];
  const stocks = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    (isStockRow(row) ? stocks : options).push(row);
  });
  return { options, stocks };
}

/**
 * Shares controlled per unit held. Mirrors SavedContract.share_multiplier on
 * the backend: an option contract covers 100 shares, a share covers itself.
 */
export function shareMultiplier(row) {
  return isStockRow(row) ? 1 : 100;
}

/**
 * Cost basis and current market value for one position, in dollars.
 * Reads the snake_case fields the saved-contract API returns.
 */
export function positionValues(contract) {
  const multiplier = shareMultiplier(contract);
  const units = Number(contract?.number_of_contracts) || 0;
  const avgCost = Number(contract?.average_cost_per_contract) || 0;
  const currentPrice = Number(contract?.current_premium) || 0;

  const costBasis = units * avgCost * multiplier;
  const marketValue = units * currentPrice * multiplier;

  // A short position profits when the price falls, so its P&L is inverted —
  // the same convention the backend's premium/equity % change methods use.
  const isShort = String(contract?.position_type || '').toLowerCase() === 'sell';
  const rawPnl = marketValue - costBasis;
  const pnl = isShort ? -rawPnl : rawPnl;

  return {
    costBasis,
    marketValue,
    pnl,
    pnlPercent: costBasis > 0 ? (pnl / costBasis) * 100 : 0,
    isShort,
  };
}
